import { CarouselContainer } from '@components/container/carousel';
import { getStay } from '@api/store';
import { allAmenities } from '@components/contants';
import { TStay } from '@components/types';
import { getLocation } from '@core/geolocation';
import { getSafeUrl, getStoreAmenityIds, humanizeCurrency, sanityIoImageLoader } from '@core/utils';
import { getDistance } from 'geolib';
import * as LucideIcons from 'lucide-react';
import { MapPin, Navigation, Star } from 'lucide-react';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface StoreTopCardProps {
  store: TStay;
  fromAllStaysPage?: boolean;
  // Listing/search page this card is shown on; becomes the stay page's "All Hotels" back target.
  refPath?: string;
}

const MAX_AMENITIES = 6;

// The stays list endpoint does not return storemeta, so a card looks the property's amenities up
// once (shared by every card/page view) instead of showing a made-up list.
const loadAmenityIds = (stay: TStay) => {
  return getStoreAmenityIds(stay)
};

function formatDistance(d: number) {
  if (d < 1000) return `${d.toFixed(0)} m`;
  if (d < 10000) return `${(d / 1000).toFixed(1)} km`;
  return `${(d / 1000).toFixed(0)} km`;
}

export function StayCard({ store, fromAllStaysPage = false, refPath }: StoreTopCardProps) {
  const [localDistance, setLocalDistance] = useState<number>();
  const listedAmenityIds = getStoreAmenityIds(store);
  const [fetchedAmenityIds, setFetchedAmenityIds] = useState<number[]>([]);

  useEffect(() => {
    if (listedAmenityIds.length > 0 || !(store?.id > 0)) return;
    setFetchedAmenityIds(loadAmenityIds(store));
  }, [store?.id]);

  const amenities = (listedAmenityIds.length > 0 ? listedAmenityIds : fetchedAmenityIds)
    .map((id) => allAmenities.find((x) => x.id === id))
    .filter(Boolean)
    .slice(0, MAX_AMENITIES);

  useEffect(() => {
    (async () => {
      const location = await getLocation();
      const distance = getDistance(
        { latitude: location?.latitude || 27.504, longitude: location?.longitude || 77.669 },
        {
          latitude: store?.address?.latitude || store?.address?.city?.latitude || 27.504,
          longitude: store?.address?.longitude || store?.address?.city?.longitude || 77.669,
        }
      );
      setLocalDistance(distance);
    })();
  }, [store]);

  const storeUrl = `/stays/${store?.id}/${getSafeUrl(store?.name)}${fromAllStaysPage ? `?source=all-stays&ref=${encodeURIComponent(refPath || '/stays')}` : ''}`;
  const hasRating = store.stats?.rating_overall > 0;
  const tagline = store?.description?.trim();
  // "New" only holds for the first 2 months after the store was added —
  // afterwards it should just fall away rather than linger indefinitely.
  const isNew = !!store?.added_on && moment().diff(moment(store.added_on), 'months', true) < 2;

  return (
    <div className="block group relative">
      {/* Full-cover link makes the whole card clickable/keyboard-focusable without
          nesting an <a> inside the card's real links (city name) below — nested
          anchors are invalid HTML and were causing a React hydration error. */}
      <Link href={storeUrl} className="absolute inset-0 z-0 rounded-[20px]" aria-label={store?.name} />
      <style jsx>{`
        .stay-card {
          background: linear-gradient(145deg, #ffffff 0%, #fbfcfe 60%, #f3f6fb 100%);
          border: 1px solid rgba(31, 58, 92, 0.1);
          border-radius: 20px;
          box-shadow:
            0 1px 0 0 rgba(255,255,255,0.9) inset,
            0 -1px 0 0 rgba(31,58,92,0.06) inset,
            0 4px 24px rgba(31, 58, 92, 0.07),
            0 1px 4px rgba(0,0,0,0.04);
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
          overflow: hidden;
          position: relative;
        }
        .stay-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 50%);
          border-radius: 20px;
          pointer-events: none;
          z-index: 1;
        }
        .stay-card:hover {
          transform: translateY(-3px);
          box-shadow:
            0 1px 0 0 rgba(255,255,255,0.9) inset,
            0 -1px 0 0 rgba(237,120,41,0.25) inset,
            0 12px 40px rgba(31, 58, 92, 0.14),
            0 4px 12px rgba(0,0,0,0.06);
        }
        .image-wrap {
          border-radius: 14px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
        }
        .image-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 50%);
          pointer-events: none;
        }
        .rating-badge {
          background: linear-gradient(135deg, #1F3A5C, #2d5382);
          box-shadow: 0 2px 8px rgba(31,58,92,0.4), inset 0 1px 0 rgba(255,255,255,0.2);
        }
        .new-badge {
          background: linear-gradient(135deg, #059669, #10b981);
          box-shadow: 0 2px 8px rgba(5,150,105,0.35), inset 0 1px 0 rgba(255,255,255,0.2);
        }
        .amenity-pill {
          background: rgba(255,255,255,0.7);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(31,58,92,0.12);
          border-radius: 20px;
          padding: 3px 10px 3px 7px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-weight: 500;
          color: #1F3A5C;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.8);
          white-space: nowrap;
        }
        .cta-btn {
          background: #0F6E56;
          border-radius: 10px;
          padding: 8px 20px;
          font-family: Manrope;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: white;
          box-shadow:
            0 4px 14px rgba(15,110,86,0.4),
            inset 0 1px 0 rgba(255,255,255,0.25);
          transition: all 0.2s ease;
          white-space: nowrap;
        }
        .cta-btn:hover {
          background: #0C5A46;
          box-shadow: 0 6px 20px rgba(15,110,86,0.5), inset 0 1px 0 rgba(255,255,255,0.25);
          transform: translateY(-1px);
        }
        .price-highlight {
          background: linear-gradient(135deg, #1F3A5C, #ED7829);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 800;
          font-size: 20px;
        }
        .divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(31,58,92,0.15), transparent);
          margin: 10px 0;
        }
        .city-link {
          color: #1F3A5C;
          font-weight: 600;
          text-decoration: none;
        }
        .city-link:hover { text-decoration: underline; }
        .dist-chip {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          background: rgba(237,120,41,0.08);
          border: 1px solid rgba(237,120,41,0.2);
          border-radius: 20px;
          padding: 1px 8px;
          font-size: 11px;
          color: #ED7829;
          font-weight: 500;
        }
      `}</style>

      <div className="stay-card p-4 relative z-10 pointer-events-none">
        <div className="grid md:grid-cols-5 grid-cols-1 gap-4 relative z-10 pointer-events-none">

          {/* ── Image column ── */}
          <div className="col-span-2 md:col-span-2">
            <div className="image-wrap max-h-[360px] min-h-[160px] md:min-h-[200px] pointer-events-auto">
              <CarouselContainer>
                <SwiperSlide style={{ height: 'auto' }} className="min-w-0">
                  {/* This wrapper is pointer-events-auto, so it swallows clicks meant for the
                      card-wide link underneath; the image needs its own link to the stay page. */}
                  <Link href={storeUrl} aria-label={store?.name} className="block h-full cursor-pointer">
                    <Image
                      loader={sanityIoImageLoader}
                      src={store?.image || 'https://cdn.jhattse.com/public/consumer/placeholder/store.png'}
                      className="object-cover w-full h-full md:h-[210px] h-[160px]"
                      alt={store?.name}
                      priority
                      width={400}
                      height={210}
                    />
                  </Link>
                </SwiperSlide>
              </CarouselContainer>
            </div>
          </div>

          {/* ── Content column ── */}
          <div className="col-span-3 flex flex-col gap-2.5">

            {/* Name + Badge */}
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base md:text-xl font-bold text-neutral-800 leading-tight line-clamp-2 font-manrope">
                {store?.name}
              </h2>
              {hasRating ? (
                <div className={`rating-badge flex-shrink-0 h-6 px-2.5 flex items-center gap-1 rounded-lg`}>
                  <Star className="w-3.5 h-3.5 text-amber-300 fill-amber-300" />
                  <span className="text-xs text-white font-bold tracking-wide">
                    {store.stats?.rating_overall}
                  </span>
                </div>
              ) : isNew ? (
                <div className="new-badge flex-shrink-0 h-6 px-2.5 flex items-center rounded-lg">
                  <span className="text-xs text-white font-semibold tracking-wider">New</span>
                </div>
              ) : null}
            </div>

            {/* Store description (if set) */}
            {tagline && (
              <p className="text-sm text-neutral-500 italic leading-snug line-clamp-2 -mt-1">{tagline}</p>
            )}

            {/* Location */}
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
              <MapPin className="w-3.5 h-3.5 text-[#ED7829] flex-shrink-0" />
              <span>
                <Link
                  href={{
                    pathname: `/stays/in/${getSafeUrl(store?.address?.city?.name || '').replace(/\s+/g, '-').toLowerCase()}-hotels`,
                    // The store's real city_id is the source of truth from the backend —
                    // the city page prefers it over its own static slug→id map, which can
                    // drift out of sync and otherwise return the wrong city's listings.
                    query: store?.address?.city_id ? { city_id: store.address.city_id } : undefined,
                  }}
                  className="city-link pointer-events-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {store?.address?.city?.name}
                </Link>
                {store?.address?.state?.name && (
                  <span className="text-neutral-500">, {store?.address?.state?.name}</span>
                )}
              </span>
              {localDistance !== undefined && localDistance < 100_000 && (
                <span className="dist-chip">
                  <Navigation className="w-3 h-3" />
                  {formatDistance(localDistance)} away
                </span>
              )}
            </div>

            <div className="divider" />

            {/* Amenities — the property's own selection (storemeta.settings.amenities) */}
            {amenities.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {amenities.map((amenity) => {
                  const Icon = (LucideIcons as any)[amenity.icon] as React.FC<{ className?: string }> | undefined;
                  return (
                    <span key={amenity.id} className="amenity-pill">
                      {Icon && <Icon className="w-3.5 h-3.5" />}
                      {amenity.name}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Price + CTA */}
            <div className="flex items-center justify-between gap-3 mt-auto">
              <div className="flex flex-col">
                <span className="text-xs text-neutral-400 font-medium">Starting from</span>
                <span className="price-highlight">{humanizeCurrency(store?.min_price || 999)}<span className="text-sm font-medium text-neutral-400" style={{ WebkitTextFillColor: '#9ca3af' }}>/night</span></span>
              </div>
              <button
                className="cta-btn pointer-events-auto"
                onClick={(e) => { e.preventDefault(); window.location.href = storeUrl; }}
              >
                Check Availability →
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
